<template>
  <Transition name="diztree-intro-fade">
    <div
      v-if="visible"
      class="diztree-intro"
      role="button"
      tabindex="0"
      :aria-label="$t('landing.intro_skip')"
      @click="dismiss"
      @keydown="dismiss"
    >
      <div class="diztree-intro-logo">
        <DiztreeLogo3D :rotate-speed="0.9" :fit-size="3.2" />
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { defineAsyncComponent } from 'vue'
// Async so the Three.js + 6.4 MB .glb chunk is fetched only when the intro
// actually renders — i.e. desktop, motion-allowed, first-visit. On phones we
// never render it, so this import never resolves and the model never downloads.
const DiztreeLogo3D = defineAsyncComponent(() => import('~/components/Home/DiztreeLogo3D.vue'))

const STORAGE_KEY = 'diztree_intro_seen_v1'
// Splash budget: anything blocking the value proposition costs visitors.
// 1.1 s is enough for the brand beat; it used to be 2.8 s.
const DURATION_MS = 1100

const visible = ref(false)

let dismissed = false
function dismiss() {
  if (dismissed) return
  dismissed = true
  visible.value = false
  try { localStorage.setItem(STORAGE_KEY, '1') } catch {}
  removeSkipListeners()
}

// Any scroll/wheel/touch attempt means the visitor wants the page — get out
// of the way immediately instead of making them wait out the timer.
function removeSkipListeners() {
  if (typeof window === 'undefined') return
  window.removeEventListener('wheel', dismiss)
  window.removeEventListener('touchmove', dismiss)
  window.removeEventListener('keydown', dismiss)
}

onMounted(() => {
  if (typeof window === 'undefined') return
  // A full-screen 3D splash is exactly the wrong thing to make a phone user
  // wait through. Skip it on small viewports and for reduced-motion — the
  // hero's static mark already carries the brand on those.
  const skip =
    !window.matchMedia('(min-width: 769px)').matches
    || window.matchMedia('(prefers-reduced-motion: reduce)').matches
    || (navigator.hardwareConcurrency != null && navigator.hardwareConcurrency < 4)
  if (skip) return

  let seen = false
  try { seen = localStorage.getItem(STORAGE_KEY) === '1' } catch {}
  if (seen) return
  visible.value = true
  window.addEventListener('wheel', dismiss, { passive: true })
  window.addEventListener('touchmove', dismiss, { passive: true })
  window.addEventListener('keydown', dismiss)
  setTimeout(dismiss, DURATION_MS)
})

onBeforeUnmount(() => removeSkipListeners())
</script>

<style scoped>
.diztree-intro {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #000;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.diztree-intro-logo {
  width: min(560px, 72vw);
  height: min(560px, 72vw);
  animation: introLogoIn 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes introLogoIn {
  from { opacity: 0; transform: scale(0.6); }
  to   { opacity: 1; transform: scale(1); }
}

.diztree-intro-fade-enter-active,
.diztree-intro-fade-leave-active {
  transition: opacity 0.45s ease;
}
.diztree-intro-fade-enter-from,
.diztree-intro-fade-leave-to {
  opacity: 0;
}
</style>
